import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartStraight, ShieldCheck, Sparkle, Users, ChatCircleText, Flower, EnvelopeSimple, Check, Quotes } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HUBS } from "@/lib/constants";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const JOURNAL_IMG = "https://images.unsplash.com/photo-1681396059172-7532b1ef8b47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxoYW5kcyUyMGhvbGRpbmclMjBqb3VybmFsJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc4NDA2Njk2NXww&ixlib=rb-4.1.0&q=85";
const BOTANICAL_IMG = "https://images.unsplash.com/photo-1633169621790-71e519cfb42d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwYm90YW5pY2FsJTIwc2FnZSUyMGxlYWYlMjBzaGFkb3d8ZW58MHx8fHwxNzg0MDY2OTY1fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  usePageMeta({
    title: "Home",
    description: "Wingwomen matches you with women who share your reproductive-health journey, endometriosis, PMOS, IVF, pregnancy, postpartum, and pairs you with a warm AI Health Navigator.",
  });
  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Nav */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flower size={26} weight="duotone" className="text-coral-700" />
            <span className="font-display text-2xl">Wingwomen</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="landing-signin" className="text-sm hover:text-coral-700 transition-colors">Sign in</Link>
            <Link to="/auth?mode=register" data-testid="landing-cta-nav"
                  className="text-sm bg-ink text-cream px-4 py-2 rounded-full hover:bg-coral transition-colors">
              Join the circle
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main id="main-content" tabIndex={-1}>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="flex items-center text-xs tracking-[0.25em] uppercase text-sage-600 mb-8">
            <span className="rule" /> Lived experience intelligence · HIPAA-aligned
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Find the women<br />
            <span className="italic text-coral-700">who've walked your path.</span>
          </h1>
          <p className="mt-8 text-lg text-ink/80 max-w-xl leading-relaxed">
            Wingwomen privately matches you with peers who share your reproductive-health journey, 
            endometriosis, PMOS, IVF, pregnancy, postpartum, and pairs you with a warm AI Navigator
            trained to listen, not prescribe.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/auth?mode=register" data-testid="hero-cta-join"
                  className="group inline-flex items-center gap-2 bg-coral text-white px-6 py-3.5 rounded-full font-medium shadow-lift hover:bg-coral-600 transition-colors">
              Start your journey <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link to="/auth" data-testid="hero-cta-login"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-ink/20 hover:border-ink transition-colors">
              I already have an account
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            <Stat n="2021" l="Founded" />
            <Stat n="100%" l="Shared anonymously" />
            <Stat n="24h" l="Disappearing chats" />
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="relative rounded-2xl overflow-hidden shadow-hero noise">
            <img src={HERO_IMG} alt="Women supporting each other" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-lift max-w-[260px] hidden md:block">
            <div className="text-xs tracking-[0.2em] uppercase text-sage-600">92% match</div>
            <div className="font-display text-lg mt-1 leading-snug">Endometriosis · IVF · Age 35–40</div>
            <div className="text-xs text-muted-foreground mt-2">Shared: pelvic pain · laparoscopy · TTC</div>
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="bg-sage-50 border-y border-sage-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-8">
          {[
            { icon: Users, title: "Similar peers", body: "AI matches you on condition, treatment, life stage, not diagnosis alone." },
            { icon: ChatCircleText, title: "AI Navigator", body: "A conversational companion for questions, prep, and clarity." },
            { icon: HeartStraight, title: "Anonymous care", body: "Pseudonymous by default. Disappearing messages if you choose." },
            { icon: ShieldCheck, title: "Private by design", body: "HIPAA-aligned architecture. Your journey stays yours." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex flex-col gap-3">
              <Icon size={30} weight="duotone" className="text-coral-700" />
              <div className="font-display text-xl">{title}</div>
              <p className="text-sm text-ink/70 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hubs */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-6">
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
              <span className="rule" /> Condition hubs
            </div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              A private circle for every part of the journey.
            </h2>
          </div>
          <p className="md:col-span-5 md:col-start-8 text-ink/70 leading-relaxed">
            Join hubs where women share what actually worked, what hurt, and what they wish they'd known.
            Curated by lived experience, not clickbait.
          </p>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 stagger">
          {HUBS.map((h) => (
            <Link key={h.id} to="/auth" data-testid={`hub-tile-${h.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-white p-6 hover:shadow-lift transition-shadow">
              <Sparkle size={22} weight="duotone" className="text-sage" />
              <div className="font-display text-2xl mt-4">{h.id}</div>
              <p className="text-sm text-ink/60 mt-2 leading-relaxed">{h.desc}</p>
              <div className="mt-6 flex items-center text-xs tracking-[0.2em] uppercase text-coral-700">
                Enter hub <ArrowRight size={14} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Wingwomen, Founder story */}
      <section className="bg-sage-50 border-y border-sage-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase text-sage-700 mb-4"><span className="rule" />A note from our founder</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              Why <span className="italic text-coral-700">Wingwomen</span>.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 relative">
            <Quotes size={38} weight="fill" className="text-coral/30 absolute -top-2 -left-6 hidden md:block" />
            <div className="space-y-5 text-ink/85 leading-relaxed font-display text-lg">
              <p>
                After the birth of my son, I found myself struggling with postpartum challenges
                in ways I never expected. I was well established in my career, but I didn't have
                a community I could turn to or a safe place to admit I was having a hard time.
                Like so many women, I felt isolated, and I worried about protecting my privacy
                and professional identity while seeking support.
              </p>
              <p>
                What changed everything was realizing how many other women were carrying the same
                struggles in silence.
              </p>
              <p>
                In 2021, I started imagining a different kind of support system, one where women
                could connect anonymously with others who truly understood their reproductive
                health journey, whether that was IVF, PMOS, endometriosis, pregnancy, postpartum
                recovery, or pregnancy loss.
              </p>
              <p>
                That's why Wingwomen exists. Every match, every conversation, and every AI-guided
                connection is built around one belief: <span className="italic text-coral-700">no
                woman should have to navigate her reproductive health journey alone.</span>
              </p>
              <p className="text-sm text-sage-700 pt-3 not-italic font-sans tracking-wide">
                Adonica Shaw, Founder
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={JOURNAL_IMG} alt="Hands holding a journal" className="rounded-2xl w-full h-[420px] object-cover shadow-hero" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="text-xs tracking-[0.25em] uppercase text-coral-700 mb-4"><span className="rule bg-coral opacity-100" />Our promise</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              Lived experience is medicine, too.
            </h2>
            <p className="mt-6 text-cream/80 leading-relaxed max-w-lg">
              For too long, women have Googled at midnight, whispered in group chats, and waited months
              for answers that never came. Wingwomen exists so that the woman who's been through it , 
              the one you desperately need to talk to, is one match away.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Endometriosis","PMOS","IVF","Pregnancy","Pregnancy Loss","Postpartum","Fibroids","Menopause"].map(t => (
                <span key={t} className="text-xs px-3 py-1.5 rounded-full border border-cream/20 text-cream/80">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <Waitlist />
      </main>

      <SiteFooter />
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-display text-3xl">{n}</div>
    <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground mt-1">{l}</div>
  </div>
);

function Waitlist() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", { email, source: "landing" });
      setDone(true); setAlready(!!data.already);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please enter a valid email");
    }
    setBusy(false);
  };

  return (
    <section className="relative overflow-hidden">
      <img src={BOTANICAL_IMG} alt="" aria-hidden
           className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-700 mb-4 flex items-center justify-center gap-2">
          <EnvelopeSimple size={14} weight="duotone" /> The Wingwomen letter
        </div>
        <h3 className="font-display text-4xl sm:text-5xl leading-tight">
          Stories, quiet news,<br />
          <span className="italic text-coral-700">sent gently.</span>
        </h3>
        <p className="mt-5 text-ink/70 max-w-xl mx-auto leading-relaxed">
          Subscribe to the Wingwomen letter for new condition-hub launches, founder
          notes, and occasional lived-experience essays. No spam, unsubscribe any time.
        </p>

        {done ? (
          <div className="mt-10 inline-flex items-center gap-3 bg-white border border-sage-200 rounded-full px-6 py-4 shadow-card" data-testid="waitlist-success">
            <div className="w-8 h-8 rounded-full bg-sage-100 grid place-items-center">
              <Check size={16} weight="bold" className="text-sage-700" />
            </div>
            <div className="text-left">
              <div className="font-display text-lg leading-tight">
                {already ? "Already on the list." : "You're subscribed."}
              </div>
              <div className="text-xs text-muted-foreground">Look for a warm hello in your inbox soon.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="waitlist-form"
                className="mt-10 max-w-md mx-auto flex flex-col sm:flex-row gap-3 bg-white/80 backdrop-blur border border-border rounded-full p-1.5 shadow-card">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="waitlist-email"
              className="flex-1 bg-transparent px-5 py-3 outline-none text-ink placeholder:text-muted-foreground"
            />
            <button type="submit" disabled={busy}
                    data-testid="waitlist-submit"
                    className="bg-coral text-white px-5 py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-60 transition-colors whitespace-nowrap">
              {busy ? "Sending…" : "Subscribe"}
            </button>
          </form>
        )}

        <div className="mt-8 text-xs tracking-[0.2em] uppercase text-muted-foreground">
          Or <Link to="/auth?mode=register" data-testid="waitlist-alt-cta" className="text-coral-700 underline underline-offset-4 hover:text-coral-600">create your free account →</Link>
        </div>
      </div>
    </section>
  );
}
